import Konva from 'konva';
import {
    CenterLayer, CustomizerEngine, FitLayer, FlipLayerHorizontal, FlipLayerVertical,
    HtmlTransformer, ImageLayer, ResetLayer, RotateLayer, WireProcessedImageSync,
} from 'smg/customizer';
import { SetupClickAwayDeselect } from 'smg/customizer/click-away';
import { DownloadURL } from 'smg/download';
import { ShowError } from 'smg/errors';
import * as html from 'smg/html';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import * as uploadClaims from 'smg/upload-claims';
import { Upload, UploadClaimFromURL, Uploader } from 'smg/uploader/v2';
import { UUID } from 'smg/uuid';
import Sortable from 'sortablejs';
import { AiImageActionsContext, RemoveLayerBackground, UpscaleLayerImage } from '../product-customizer/ai-image-actions';
import { BuilderLayerList, LayerAction, LayerRow, LayerRowProvider } from '../product-customizer/layer-list';
import { PrintResolutionLayer, PrintResolutionWarning } from '../product-customizer/print-resolution-warning';
import { UploadPreviewURL } from '../product-customizer/upload-preview';
import { COIN_DPI } from './coin-text-layer';
import { TextDropUI } from './text-drop-ui';

interface uploadedImage {
    layerID: string;
    uploadID: number;
    side: 'front' | 'back';
    url: string;
    /** Original filename from upload */
    filename: string;
    /** Rasterized preview URL for vector files (PDF, AI, EPS) */
    previewUrl?: string;
    /** Permanent URL (cached after keep endpoint succeeds) */
    permanentUrl?: string;
    /** S3 key proof for keep-by-proof-of-key */
    claim?: string;
    /** Pre-removal url/id, stashed so restoring the background can revert. */
    originalUrl?: string;
    originalUploadID?: number;
    /** Pre-upscale url/id, stashed so restoring the upscale can revert. */
    preUpscaleUrl?: string;
    preUpscaleUploadID?: number;
}

/** Serialized image state for persistence */
export interface ImageState {
    uploadID: number;
    side: 'front' | 'back';
    position: { x: number; y: number };
    scale: { x: number; y: number };
    rotation: number;
    /** Natural (unscaled) dimensions at time of save - used to adjust scale on restore */
    naturalSize?: { width: number; height: number };
    /** Actual Konva z-index (rendering order) at time of save */
    actualZIndex?: number;
    /** Permanent URL (set after calling keep endpoint) */
    url?: string;
    /** Original filename from upload */
    filename?: string;
    /** Whether the layer is visible on the canvas */
    visible?: boolean;
    /** url is already an upscaled result (persisted so reload doesn't re-upscale it). */
    isUpscaled?: boolean;
    upscaledUploadID?: number;
}

const CANVAS_CENTER = 969 / 2;

/** Mask radius depends on whether the text ring is enabled */
function getCurrentMaskRadius(): number {
    const $toggle = document.getElementById('wants-text-ring-switch') as HTMLInputElement | null;
    return $toggle?.checked ? 322 : 408;
}

export class ImageUploadUI {
    #frontEngine: CustomizerEngine;
    #backEngine: CustomizerEngine;
    #frontTransformer: HtmlTransformer;
    #backTransformer: HtmlTransformer;
    #currentSide: 'front' | 'back' = 'front';
    #uploadedImages = new Map<string, uploadedImage>();
    #selectedLayerId: string | null = null;
    #layerListContainer: HTMLElement | null = null;
    #activeMenu: HTMLElement | null = null;
    #sortable: Sortable | null = null;
    #designList: BuilderLayerList | null = null;
    #layerItemTemplate: HTMLTemplateElement | null = null;
    #addImageBtnTemplate: HTMLTemplateElement | null = null;
    #lastKonvaDragEndTime = 0;
    #mobileLayersPanel: HTMLElement | null = null;
    #mobileLayersScroll: HTMLElement | null = null;
    #mobileAddImagesBtn: HTMLElement | null = null;
    #mobileUploadZone: HTMLElement | null = null;
    #lgUploadZone: HTMLElement | null = null;
    #lgDesignUploads: HTMLElement | null = null;
    #lgDesignLayerList: HTMLElement | null = null;
    #designTabUploads: HTMLElement | null = null;
    #designTabLayerList: HTMLElement | null = null;
    #canvasContainer: HTMLElement | null = null;
    #canvasUploadOverlay: HTMLElement | null = null;
    #uploader: Uploader | null = null;
    #textDrop: TextDropUI | null = null;

    constructor(frontEngine: CustomizerEngine, backEngine: CustomizerEngine) {
        this.#frontEngine = frontEngine;
        this.#backEngine = backEngine;

        // Create HTML-based transformers that render handles outside the canvas
        // This avoids Konva's clipping which hides handles when images extend beyond the stage
        this.#frontTransformer = new HtmlTransformer(frontEngine, {
            onTargetChange: layerId => {
                this.#onTransformerTargetChange(layerId);
            },
        });
        this.#backTransformer = new HtmlTransformer(backEngine, {
            onTargetChange: layerId => {
                this.#onTransformerTargetChange(layerId);
            },
        });

        // Before the UI init: the layer list's rows read the warning's badges on render
        this.#printWarning = new PrintResolutionWarning({
            mount: () => document.getElementById('print-resolution-alerts'),
            product: 'coin',
            canvasDPI: () => COIN_DPI,
            layers: () => this.#currentSideImageLayers(),
            upscale: layerId => {
                if (!this.#isProcessing) {
                    void UpscaleLayerImage(this.#imageActionsCtx(), layerId);
                }
            },
            onChange: () => this.#renderDesignTabLayerList(),
        });
        this.#printWarning.watch(frontEngine);
        this.#printWarning.watch(backEngine);

        this.#initializeUI();
        // Keep the upload map in sync with AI bg-removal/upscale (and their undos)
        WireProcessedImageSync(this.#frontEngine, layerId => this.#uploadedImages.get(layerId));
        WireProcessedImageSync(this.#backEngine, layerId => this.#uploadedImages.get(layerId));
        this.#setupStageClickListeners();
        this.#setupContextMenu();
    }

    /** The visible side's image layers, for the print-resolution check */
    #currentSideImageLayers(): PrintResolutionLayer[] {
        const layers: PrintResolutionLayer[] = [];
        for (const img of this.#uploadedImages.values()) {
            if (img.side !== this.#currentSide) {
                continue;
            }
            const layer = this.#currentEngine.getLayer(img.layerID);
            if (layer instanceof ImageLayer) {
                layers.push({ id: img.layerID, filename: img.filename, layer });
            }
        }
        return layers;
    }

    get #currentEngine(): CustomizerEngine {
        return this.#currentSide === 'front' ? this.#frontEngine : this.#backEngine;
    }

    get #currentTransformer(): HtmlTransformer {
        return this.#currentSide === 'front' ? this.#frontTransformer : this.#backTransformer;
    }

    #uploadInputContainer: HTMLElement | null = null;
    #uploadHeading: HTMLElement | null = null;
    #fileInput: HTMLInputElement | null = null;
    #batchUploadTotal: number = 0;
    #batchUploadCurrent: number = 0;
    #uploadingSide: 'front' | 'back' = 'front';
    #isUploading = false;
    #pendingUploads = new Map<Upload, { side: 'front' | 'back'; filename: string }>();
    #processingCount = 0; // Tracks background removal and similar operations
    #printWarning: PrintResolutionWarning;

    get #isProcessing(): boolean {
        return this.#isUploading || this.#processingCount > 0;
    }

    #initializeUI() {
        const $uploadLabel = document.querySelector('.uploads-container-label') as HTMLLabelElement;
        const $dropZone = document.getElementById('coin-preview-container');
        this.#canvasContainer = $dropZone;
        this.#layerListContainer = document.getElementById('uploaded-images-list');
        this.#uploadInputContainer = $uploadLabel;
        // Find the "Upload Image" heading (previous sibling of the label)
        this.#uploadHeading = $uploadLabel?.previousElementSibling as HTMLElement;

        if (!$uploadLabel) {
            console.error('uploads-container-label not found');
            return;
        }

        if (!this.#layerListContainer) {
            console.error('uploaded-images-list not found');
            return;
        }

        this.#layerItemTemplate = document.getElementById('layer-item-template') as HTMLTemplateElement;
        this.#addImageBtnTemplate = document.getElementById('add-image-btn-template') as HTMLTemplateElement;

        if (!this.#layerItemTemplate) {
            console.error('layer-item-template not found');
            return;
        }

        if (!this.#addImageBtnTemplate) {
            console.error('add-image-btn-template not found');
            return;
        }

        // Mobile image layers panel references
        this.#mobileLayersPanel = document.getElementById('mobile-image-layers');
        this.#mobileLayersScroll = document.getElementById('mobile-layers-scroll');
        this.#mobileAddImagesBtn = document.querySelector('.add-images-btn');
        this.#mobileUploadZone = document.getElementById('upload-zone');
        this.#lgUploadZone = document.getElementById('lg-upload-zone');
        this.#lgDesignUploads = document.getElementById('lg-design-uploads');
        this.#lgDesignLayerList = document.getElementById('lg-design-layer-list');
        this.#designTabUploads = document.getElementById('design-uploads');
        this.#designTabLayerList = document.getElementById('design-layer-list');

        // The page's <input> carries `accept` (rendered from the builder's spec sheet) and
        // `multiple`. Mobile browsers offer the camera in their native picker on their own.
        const $fileInput = document.getElementById('upload-input') as HTMLInputElement;
        this.#fileInput = $fileInput;

        // Note: Radio button listeners are set up in the main page initialization
        // which calls setSide() when the side changes (see no-minimum-custom-coins.ts)

        // Use the new Uploader v2 with better lifecycle hooks
        this.#uploader = new Uploader({
            $input: () => $fileInput,
            $dropzones: () => {
                const zones: HTMLElement[] = [];
                if ($dropZone) {
                    zones.push($dropZone);
                }
                if ($uploadLabel) {
                    zones.push($uploadLabel);
                }
                if (this.#layerListContainer) {
                    zones.push(this.#layerListContainer);
                }
                if (this.#mobileUploadZone) {
                    zones.push(this.#mobileUploadZone);
                }
                if (this.#lgUploadZone) {
                    zones.push(this.#lgUploadZone);
                }
                if (this.#designTabLayerList) {
                    zones.push(this.#designTabLayerList);
                }
                if (this.#lgDesignLayerList) {
                    zones.push(this.#lgDesignLayerList);
                }
                return zones;
            },

            $pasteTargets: () => [document.body],

            // Block every intake path (picker, drop, paste, addBlob) while a
            // process runs — a new upload would land on the frozen side
            disabled: () => this.#isProcessing,

            onDragStart: () => {
                $uploadLabel?.classList.add('drag-over');
                this.#layerListContainer?.classList.add('drag-over');
                this.#mobileUploadZone?.classList.add('drag-over');
                this.#lgUploadZone?.classList.add('drag-over');
                this.#designTabLayerList?.classList.add('drag-over');
                this.#lgDesignLayerList?.classList.add('drag-over');
            },

            onDragStop: () => {
                $uploadLabel?.classList.remove('drag-over');
                this.#layerListContainer?.classList.remove('drag-over');
                this.#mobileUploadZone?.classList.remove('drag-over');
                this.#lgUploadZone?.classList.remove('drag-over');
                this.#designTabLayerList?.classList.remove('drag-over');
                this.#lgDesignLayerList?.classList.remove('drag-over');
            },

            // onStart fires synchronously when files are selected - perfect for capturing side
            onStart: (upload: Upload) => {
                const filename = upload.name || `img${upload.id ?? 0}.jpg`;
                this.#pendingUploads.set(upload, { side: this.#currentSide, filename });
                this.#uploadingSide = this.#currentSide;
                this.#isUploading = true;
                this.#setInteractionEnabled(false);

                this.#batchUploadTotal++;
            },

            onLoaderStart: () => {
                this.#showLoadingState();
            },

            onComplete: async (upload: Upload) => {
                const pending = this.#pendingUploads.get(upload);
                const targetSide = pending?.side ?? this.#uploadingSide;
                const filename = pending?.filename ?? (upload.name || `img${upload.id ?? 0}.jpg`);
                this.#pendingUploads.delete(upload);

                this.#batchUploadCurrent++;
                this.#updateBatchProgress();

                const blobURL = upload.data ? URL.createObjectURL(upload.data) : '';
                const uploadID = upload.id ?? 0;

                await this.#handleUploadComplete(uploadID, blobURL, targetSide, filename, upload.tmpKey);

                if (this.#pendingUploads.size === 0) {
                    this.#isUploading = false;
                    this.#batchUploadTotal = 0;
                    this.#batchUploadCurrent = 0;

                    // Wait for all thumbnail images to finish loading before hiding spinner.
                    // img-only: text block rows reuse .design-layer-thumb on a div, which
                    // has no load lifecycle and would hang this await forever
                    const thumbs = document.querySelectorAll<HTMLImageElement>('img.design-layer-thumb, img.mobile-layer-thumb, img.layer-thumbnail');
                    await Promise.all(Array.from(thumbs).map(img =>
                        img.complete ? Promise.resolve() : new Promise(r => {
                            img.onload = r;
                            img.onerror = r;
                        }),
                    ));

                    this.#hideLoadingState();
                    if (!this.#isProcessing) {
                        this.#setInteractionEnabled(true);
                    }
                }
            },

            onError: (upload: Upload, err: unknown) => {
                this.#pendingUploads.delete(upload);
                this.#batchUploadCurrent++;

                if (this.#pendingUploads.size === 0) {
                    this.#isUploading = false;
                    this.#batchUploadTotal = 0;
                    this.#batchUploadCurrent = 0;
                    this.#hideLoadingState();
                    if (!this.#isProcessing) {
                        this.#setInteractionEnabled(true);
                    }
                } else {
                    this.#updateBatchProgress();
                }

                ShowError(err);
            },

        });
    }

    #showLoadingState() {
        const message = this.#getBatchProgressMessage();

        if (this.#canvasContainer && !this.#canvasUploadOverlay) {
            this.#canvasUploadOverlay = html.FromString(`
                <div class="coin-loading-overlay">
                    <div class="d-flex flex-column align-items-center gap-2">
                        <div class="spinner-border text-secondary" role="status">
                            <span class="visually-hidden">${message}</span>
                        </div>
                        <span class="fw-600 fs-7 upload-overlay-message">${message}</span>
                    </div>
                </div>
            `).firstElementChild as HTMLElement;
            this.#canvasContainer.appendChild(this.#canvasUploadOverlay);
        }

        if (this.#uploadInputContainer && !this.#uploadInputContainer.classList.contains('d-none')) {
            this.#uploadInputContainer.classList.add('uploading');
            this.#uploadInputContainer.appendChild(this.#createSpinner(message));
        }

        if (this.#layerListContainer && !this.#layerListContainer.classList.contains('hidden')) {
            this.#layerListContainer.classList.add('uploading');
            this.#layerListContainer.appendChild(this.#createSpinner(message));
        }

        if (this.#mobileUploadZone && this.#mobileUploadZone.offsetParent !== null) {
            this.#mobileUploadZone.classList.add('uploading');
            this.#mobileUploadZone.appendChild(this.#createSpinner(message));
        }

        if (this.#lgUploadZone && this.#lgUploadZone.offsetParent !== null) {
            this.#lgUploadZone.classList.add('uploading');
            this.#lgUploadZone.appendChild(this.#createSpinner(message));
        }

        for (const $list of [this.#designTabLayerList, this.#lgDesignLayerList]) {
            if ($list) {
                $list.classList.add('uploading');
                $list.appendChild(this.#createSpinner(message));
            }
        }
    }

    #updateBatchProgress() {
        const message = this.#getBatchProgressMessage();

        const uploadSpinner = this.#uploadInputContainer?.querySelector('.upload-spinner');
        if (uploadSpinner) {
            const messageEl = uploadSpinner.querySelector('.visually-hidden');
            if (messageEl) {
                messageEl.textContent = message;
            }
        }

        const layerSpinner = this.#layerListContainer?.querySelector('.upload-spinner');
        if (layerSpinner) {
            const messageEl = layerSpinner.querySelector('.visually-hidden');
            if (messageEl) {
                messageEl.textContent = message;
            }
        }

        if (this.#canvasUploadOverlay) {
            for (const $el of this.#canvasUploadOverlay.querySelectorAll('.visually-hidden, .upload-overlay-message')) {
                $el.textContent = message;
            }
        }
    }

    #getBatchProgressMessage(): string {
        if (this.#batchUploadTotal > 1) {
            return `Uploading ${this.#batchUploadCurrent} of ${this.#batchUploadTotal} images...`;
        }
        return 'Uploading...';
    }

    #hideLoadingState() {
        this.#canvasUploadOverlay?.remove();
        this.#canvasUploadOverlay = null;

        for (const $el of [
            this.#uploadInputContainer,
            this.#layerListContainer,
            this.#mobileUploadZone,
            this.#lgUploadZone,
            this.#designTabLayerList,
            this.#lgDesignLayerList,
        ]) {
            if ($el) {
                $el.classList.remove('uploading');
                $el.querySelector('.upload-spinner')?.remove();
            }
        }
    }

    #createSpinner(message: string = 'Uploading...'): HTMLElement {
        const spinner = document.createElement('div');
        spinner.className = 'upload-spinner';
        spinner.innerHTML = `<div class="spinner-border text-secondary" role="status"><span class="visually-hidden">${message}</span></div>`;
        return spinner;
    }

    async #handleUploadComplete(uploadID: number, blobURL: string, targetSide: 'front' | 'back', filename: string, claim?: string): Promise<void> {
        try {
            const targetEngine = targetSide === 'front' ? this.#frontEngine : this.#backEngine;

            const layerID = `img-${targetSide}-${UUID()}`;

            const imageLayer = new ImageLayer(layerID, {
                userEditable: true,
                resizable: true,
                // zIndex 7 puts uploads above ring (5) but below text (10)
                zIndex: 7,
            });

            const previewURL = await UploadPreviewURL(uploadID, blobURL, filename);
            await imageLayer.setImage(previewURL);

            const zone = targetEngine.getImageZone('coin-center');
            if (zone?.maskUrl) {
                // For back side, mask position is offset but we DON'T offset the image layer
                // The mask positioning is handled internally by ImageLayer
                // Pass the zone bounds as the cover size so the image scales to fill the visible area
                await imageLayer.setMask(zone.maskUrl, zone.maskPosition, {
                    width: zone.bounds.width,
                    height: zone.bounds.height,
                });
            }

            // Track the upload BEFORE adding to engine, since addLayer emits
            // significantChange which triggers autosave
            const uploadInfo: uploadedImage = {
                layerID,
                uploadID,
                side: targetSide,
                url: blobURL,
                previewUrl: previewURL !== blobURL ? previewURL : undefined,
                filename,
                claim,
            };
            this.#uploadedImages.set(layerID, uploadInfo);
            uploadClaims.Register([{ uploadID, claim }]);

            await targetEngine.addLayer(layerID, imageLayer);

            // Bring new upload to front so it appears at the top of the layer list
            targetEngine.bringLayerToFront(layerID);

            this.#setupLayerClickListener(imageLayer, layerID);

            if (this.#currentSide === targetSide) {
                this.#selectLayer(layerID);
            }

            toast.Success('Image uploaded successfully');
        } catch (error) {
            toast.Error('Failed to add image to canvas');
            console.error('[ImageUploadUI] Upload error:', error);
        }
    }

    #renderLayerList() {
        if (!this.#layerListContainer) {
            return;
        }

        const engine = this.#currentEngine;
        const currentSideLayers = Array.from(this.#uploadedImages.values()).
            filter(img => img.side === this.#currentSide).
            sort((a, b) => {
                const aLayer = engine.getLayer(a.layerID);
                const bLayer = engine.getLayer(b.layerID);
                const aZ = aLayer?.getActualZIndex() ?? 0;
                const bZ = bLayer?.getActualZIndex() ?? 0;
                // Descending: highest z-index (front) at top of list
                return bZ - aZ;
            });

        if (currentSideLayers.length === 0) {
            this.#layerListContainer.classList.add('hidden');
            if (this.#uploadInputContainer) {
                this.#uploadInputContainer.classList.remove('d-none');
            }
            if (this.#uploadHeading) {
                this.#uploadHeading.classList.remove('d-none');
            }
            this.#renderDesignTabLayerList();
            return;
        }

        this.#layerListContainer.classList.remove('hidden');
        // Hide the original upload input and heading when we have images
        if (this.#uploadInputContainer) {
            this.#uploadInputContainer.classList.add('d-none');
        }
        if (this.#uploadHeading) {
            this.#uploadHeading.classList.add('d-none');
        }

        this.#layerListContainer.innerHTML = '';

        currentSideLayers.forEach(layer => {
            const $item = this.#createLayerItem(layer);
            this.#layerListContainer?.appendChild($item);
        });

        if (this.#addImageBtnTemplate) {
            const $addBtn = template.Clone(this.#addImageBtnTemplate).firstElementChild as HTMLButtonElement;
            $addBtn.addEventListener('click', () => {
                this.#uploadInputContainer?.click();
            });
            this.#layerListContainer?.appendChild($addBtn);
        }

        this.#setupSortable();
        this.#renderMobileLayerList();
        this.#updateMobileVisibility();
        this.#renderDesignTabLayerList();
    }

    #renderMobileLayerList() {
        if (!this.#mobileLayersScroll) {
            return;
        }

        const engine = this.#currentEngine;
        const currentSideLayers = Array.from(this.#uploadedImages.values()).
            filter(img => img.side === this.#currentSide).
            sort((a, b) => {
                const aLayer = engine.getLayer(a.layerID);
                const bLayer = engine.getLayer(b.layerID);
                const aZ = aLayer?.getActualZIndex() ?? 0;
                const bZ = bLayer?.getActualZIndex() ?? 0;
                return bZ - aZ;
            });

        this.#mobileLayersScroll.innerHTML = '';

        currentSideLayers.forEach(layer => {
            const $wrapper = document.createElement('div');
            $wrapper.className = 'mobile-layer-thumb-wrapper';

            const $thumb = document.createElement('img');
            $thumb.className = 'mobile-layer-thumb';
            $thumb.src = layer.previewUrl ?? layer.url;
            $thumb.alt = layer.filename;

            if (this.#selectedLayerId === layer.layerID) {
                $thumb.classList.add('selected');
            }

            $thumb.addEventListener('click', () => {
                this.#selectLayer(layer.layerID);
            });

            const $menuBtn = document.createElement('button');
            $menuBtn.className = 'mobile-layer-menu-btn';
            $menuBtn.setAttribute('aria-label', 'Layer options');
            $menuBtn.innerHTML = '<i class="fas fa-ellipsis-v"></i>';
            $menuBtn.addEventListener('click', e => {
                e.stopPropagation();
                this.#selectLayer(layer.layerID);
                this.#showLayerMenu(layer.layerID, e.currentTarget as HTMLElement);
            });

            $wrapper.appendChild($thumb);
            $wrapper.appendChild($menuBtn);
            this.#mobileLayersScroll?.appendChild($wrapper);
        });
    }

    #updateMobileVisibility() {
        if (!this.#mobileLayersPanel || !this.#mobileAddImagesBtn) {
            return;
        }

        const currentSideLayers = Array.from(this.#uploadedImages.values()).
            filter(img => img.side === this.#currentSide);

        const wasVisible = !this.#mobileLayersPanel.classList.contains('d-none');
        const shouldBeVisible = currentSideLayers.length > 0;

        if (shouldBeVisible) {
            this.#mobileAddImagesBtn.classList.add('d-none');
            this.#mobileLayersPanel.classList.remove('d-none');
        } else {
            this.#mobileAddImagesBtn.classList.remove('d-none');
            this.#mobileLayersPanel.classList.add('d-none');
        }

        // When the panel visibility changes, the coin container width changes —
        // re-fit the Konva stage to match the new container size
        if (wasVisible !== shouldBeVisible) {
            requestAnimationFrame(() => {
                this.#currentEngine.fitStageIntoParentContainer();
            });
        }
    }

    /** Render the design tab's layer lists (mobile + LG) through the shared component */
    #renderDesignTabLayerList() {
        this.#designList ??= this.#buildDesignList();
        this.#designList?.render();
    }

    #buildDesignList(): BuilderLayerList | null {
        const containers = [this.#designTabLayerList, this.#lgDesignLayerList].
            filter(($el): $el is HTMLElement => Boolean($el));
        if (!containers.length) {
            return null;
        }
        return new BuilderLayerList({
            containers,
            providers: [this.#designImageProvider(), this.#designTextProvider()],
            reorder: orderedIds => {
                if (this.#isProcessing) {
                    this.#renderLayerList();
                    return;
                }
                // Top = front, bottom = back: process bottom-up, bringing each to
                // front so the last one processed ends up on top
                for (let i = orderedIds.length - 1; i >= 0; i--) {
                    this.#currentEngine.bringLayerToFront(orderedIds[i]);
                }
                // Stacking order persists (saved z-indexes) — autosave it
                this.#currentEngine.emit('significantChange', {});
                this.#renderLayerList();
            },
            onEmptyStateChange: empty => {
                this.#designTabUploads?.classList.toggle('d-none', empty);
                this.#mobileUploadZone?.classList.toggle('d-none', !empty);
                this.#lgDesignUploads?.classList.toggle('d-none', empty);
                this.#lgUploadZone?.classList.toggle('hidden', !empty);
            },
            onAddImage: () => this.#fileInput?.click(),
            onAddText: () => {
                void this.#textDrop?.addBlock();
            },
            labelElements: () => [
                this.#designTabUploads?.querySelector('.design-uploads-label') ?? null,
                this.#lgDesignUploads?.querySelector('.design-uploads-label') ?? null,
            ],
            // Re-renders clear innerHTML — keep the spinner while a batch is in flight
            extraContent: () => this.#isUploading ? this.#createSpinner(this.#getBatchProgressMessage()) : null,
            changedEventName: 'coin-uploads-changed',
        });
    }

    #designImageProvider(): LayerRowProvider {
        return {
            rows: (): LayerRow[] => Array.from(this.#uploadedImages.values()).
                filter(img => img.side === this.#currentSide).
                map(img => ({
                    id: img.layerID,
                    name: img.filename,
                    thumbUrl: img.previewUrl ?? img.url,
                    visible: this.#currentEngine.getLayer(img.layerID)?.isVisible() ?? true,
                    selected: this.#selectedLayerId === img.layerID,
                    z: this.#currentEngine.getLayer(img.layerID)?.getActualZIndex() ?? 0,
                    warning: this.#printWarning.badge(img.layerID),
                })),
            has: id => this.#uploadedImages.has(id),
            select: id => this.#selectLayer(id),
            toggleVisibility: id => this.#toggleLayerVisibility(id),
            actions: id => this.#designImageActions(id),
        };
    }

    #designTextProvider(): LayerRowProvider {
        return {
            rows: (): LayerRow[] => (this.#textDrop?.blocksForSide(this.#currentSide) ?? []).map(({ id, layer }) => ({
                id,
                name: layer.getText().trim() || 'Text',
                glyphIcon: 'fa-font',
                visible: layer.isVisible(),
                selected: this.#textDrop?.getSelectedId() === id,
                z: layer.getActualZIndex(),
            })),
            has: id => this.#textDrop?.hasBlock(id) ?? false,
            select: id => this.#textDrop?.select(id),
            toggleVisibility: id => this.#textDrop?.toggleVisibility(id),
            actions: id => this.#designTextActions(id),
        };
    }

    /** The shared image actions' view of the coin builder (current side's engine, mask-cover fit) */
    #imageActionsCtx(): AiImageActionsContext {
        return {
            engine: () => this.#currentEngine,
            center: () => ({ x: CANVAS_CENTER, y: CANVAS_CENTER }),
            // Cover the circular mask area (may overflow, clipped by the mask) — the
            // same scale uploads get on arrival
            fitScale: layer => {
                const natural = layer.getNaturalSize();
                if (natural.width === 0 || natural.height === 0) {
                    return 1;
                }
                return getCurrentMaskRadius() * 2 / Math.min(natural.width, natural.height);
            },
            canvasContainer: () => this.#canvasContainer,
            // AI processing must finish before the side can switch (the layer swap
            // mid-flight would strand the result on the wrong engine)
            setBusy: busy => {
                if (busy) {
                    this.#processingCount++;
                    this.#setInteractionEnabled(false);
                } else {
                    this.#processingCount--;
                    if (!this.#isProcessing) {
                        this.#setInteractionEnabled(true);
                    }
                }
            },
        };
    }

    /** Action-bar runs no-op while the processing lock is on (mirrors the CSS pointer lock) */
    #guardedActions(actions: LayerAction[]): LayerAction[] {
        return actions.map(action => ({
            ...action,
            run: () => {
                if (!this.#isProcessing) {
                    action.run();
                }
            },
        }));
    }

    #designImageActions(layerId: string): LayerAction[] {
        const ctx = this.#imageActionsCtx();
        // Once upscaled, drop the action — upscaleImage() is a no-op on it.
        const upscaled = this.#isLayerUpscaled(layerId);
        return this.#guardedActions([
            { icon: 'fa-redo-alt', lines: ['Rotate', 'Right'], run: () => RotateLayer(ctx, layerId, 90) },
            { icon: 'fa-undo-alt', lines: ['Rotate', 'Left'], run: () => RotateLayer(ctx, layerId, -90) },
            { icon: 'fa-arrows-alt-h', lines: ['Flip', 'Horiz.'], run: () => FlipLayerHorizontal(ctx, layerId) },
            { icon: 'fa-arrows-alt-v', lines: ['Flip', 'Vertical'], run: () => FlipLayerVertical(ctx, layerId) },
            { icon: 'fa-angle-double-up', lines: ['Bring to', 'Front'], run: () => this.#bringLayerToFront(layerId) },
            { icon: 'fa-angle-double-down', lines: ['Send to', 'Back'], run: () => this.#bringLayerToBack(layerId) },
            { icon: 'fa-expand', lines: ['Fit to', 'Screen'], run: () => FitLayer(ctx, layerId) },
            { icon: 'fa-crosshairs', lines: ['Center', 'Image'], run: () => CenterLayer(ctx, layerId) },
            {
                icon: 'fa-exchange-alt',
                lines: ['Send to', this.#currentSide === 'front' ? 'Back Side' : 'Front Side'],
                run: () => {
                    void this.#sendLayerToOtherSide(layerId);
                },
            },
            { icon: 'fa-trash-alt', lines: ['Delete', 'Image'], run: () => this.#deleteLayer(layerId) },
            {
                icon: 'fa-clone',
                lines: ['Duplicate', 'Image'],
                run: () => {
                    void this.#duplicateLayer(layerId);
                },
            },
            {
                icon: 'fa-magic',
                lines: ['AI Background', 'Removal'],
                run: () => {
                    void RemoveLayerBackground(ctx, layerId);
                },
            },
            ...upscaled ? [] : [{
                icon: 'fa-search-plus',
                lines: ['AI Upscale', 'Image'] as [string, string],
                run: () => {
                    void UpscaleLayerImage(ctx, layerId);
                },
            }],
            {
                icon: 'fa-file-import',
                lines: ['Replace', 'Image'],
                run: () => {
                    this.#fileInput?.click();
                },
            },
            {
                icon: 'fa-download',
                lines: ['Download', 'Image'],
                run: () => {
                    void this.#downloadLayerImage(layerId);
                },
            },
            { icon: 'fa-sync-alt', lines: ['Reset', 'Transform'], run: () => ResetLayer(ctx, layerId) },
        ]);
    }

    // Text blocks get a reduced action set — content/style edits live in the
    // floating toolbar, so the bar covers placement, stacking, and removal
    #designTextActions(layerId: string): LayerAction[] {
        return this.#guardedActions([
            { icon: 'fa-arrows-alt-h', lines: ['Flip', 'Horiz.'], run: () => this.#textDrop?.flipBlock(layerId, 'x') },
            { icon: 'fa-arrows-alt-v', lines: ['Flip', 'Vertical'], run: () => this.#textDrop?.flipBlock(layerId, 'y') },
            { icon: 'fa-angle-double-up', lines: ['Bring to', 'Front'], run: () => this.#bringLayerToFront(layerId) },
            { icon: 'fa-angle-double-down', lines: ['Send to', 'Back'], run: () => this.#bringLayerToBack(layerId) },
            { icon: 'fa-crosshairs', lines: ['Center', 'Text'], run: () => this.#textDrop?.centerBlock(layerId) },
            { icon: 'fa-trash-alt', lines: ['Delete', 'Text'], run: () => this.#textDrop?.deleteBlock(layerId) },
            {
                icon: 'fa-clone',
                lines: ['Duplicate', 'Text'],
                run: () => {
                    void this.#textDrop?.duplicateBlock(layerId);
                },
            },
            { icon: 'fa-sync-alt', lines: ['Reset', 'Transform'], run: () => this.#textDrop?.resetBlockTransform(layerId) },
        ]);
    }

    #isLayerUpscaled(layerId: string): boolean {
        const info = this.#uploadedImages.get(layerId);
        if (!info) {
            return false;
        }
        const engine = info.side === 'front' ? this.#frontEngine : this.#backEngine;
        const layer = engine.getLayer(layerId);
        return layer instanceof ImageLayer && layer.hasUpscaled();
    }

    #createLayerItem(layer: uploadedImage): HTMLElement {
        if (!this.#layerItemTemplate) {
            throw new Error('Layer item template not found');
        }

        const $item = template.Clone(this.#layerItemTemplate).firstElementChild as HTMLElement;
        $item.dataset.layerId = layer.layerID;

        if (this.#selectedLayerId === layer.layerID) {
            $item.classList.add('selected');
        }

        const $thumbnail = $item.querySelector('.layer-thumbnail') as HTMLImageElement;
        const $name = $item.querySelector('.layer-name') as HTMLParagraphElement;
        const $menuBtn = $item.querySelector('.layer-menu-btn') as HTMLButtonElement;

        $thumbnail.src = layer.previewUrl ?? layer.url;
        $name.textContent = layer.filename;
        $name.title = layer.filename; // Full filename on hover

        $menuBtn.addEventListener('click', e => {
            e.stopPropagation();
            this.#showLayerMenu(layer.layerID, e.currentTarget as HTMLElement);
        });

        $item.addEventListener('click', () => {
            this.#selectLayer(layer.layerID);
        });

        return $item;
    }

    #setupSortable(): void {
        this.#sortable?.destroy();
        this.#sortable = null;

        if (!this.#layerListContainer) {
            return;
        }

        const currentSideLayers = Array.from(this.#uploadedImages.values()).
            filter(img => img.side === this.#currentSide);

        if (currentSideLayers.length <= 1) {
            return;
        }

        // Re-renders mid-process recreate the sortable — carry the lock over
        this.#sortable = Sortable.create(this.#layerListContainer, {
            disabled: this.#isProcessing,
            animation: 150,
            handle: '.layer-drag-handle',
            filter: '.add-image-btn',
            ghostClass: 'sortable-ghost',
            onEnd: evt => {
                if (evt.oldIndex === undefined || evt.newIndex === undefined || evt.oldIndex === evt.newIndex) {
                    return;
                }
                this.#handleReorder();
            },
        });
    }

    #handleReorder(): void {
        const $items = this.#layerListContainer?.querySelectorAll('.upload-layer-item');
        if (!$items) {
            return;
        }

        const layerIds: string[] = [];
        $items.forEach($item => {
            const id = ($item as HTMLElement).dataset.layerId;
            if (id) {
                layerIds.push(id);
            }
        });

        // Rebuild the z-order to match the new DOM order.
        // The layer list shows top = front, bottom = back.
        // Process from bottom of the list (back) to top (front),
        // bringing each to front so the last one processed ends up on top.
        for (let i = layerIds.length - 1; i >= 0; i--) {
            this.#currentEngine.bringLayerToFront(layerIds[i]);
        }

        // Stacking order persists (saved z-indexes) — autosave it
        this.#currentEngine.emit('significantChange', {});
    }

    #showLayerMenu(layerId: string, buttonElement: HTMLElement, cursorPosition?: { x: number; y: number }) {
        if (this.#isProcessing) {
            return;
        }
        this.#closeMenu();

        // Get layers for current side to determine if we should show move options
        const currentSideLayers = Array.from(this.#uploadedImages.values()).
            filter(img => img.side === this.#currentSide);
        const showMoveOptions = currentSideLayers.length > 1;

        // Determine the layer's current side to label the Send to Other Side action
        const layerInfo = this.#uploadedImages.get(layerId);
        const otherSideLabel = layerInfo?.side === 'front' ? 'Back Side' : 'Front Side';

        const $menu = document.createElement('div');
        $menu.className = 'layer-menu-dropdown show';

        const ctx = this.#imageActionsCtx();

        // Once upscaled, drop the action — upscaleImage() is a no-op on it.
        let upscaleItem: { label: string; action: () => void; className?: string } | null = null;
        if (!this.#isLayerUpscaled(layerId)) {
            upscaleItem = {
                label: 'AI Upscale Image',
                action: () => {
                    void UpscaleLayerImage(ctx, layerId);
                },
            };
        }

        const menuItems: { label: string; action: () => void; className?: string }[] = [
            {
                label: 'Delete',
                action: () => {
                    this.#deleteLayer(layerId);
                },
                className: 'danger',
            },
            {
                label: 'Duplicate',
                action: () => {
                    void this.#duplicateLayer(layerId);
                },
            },
            {
                label: 'Download Image',
                action: () => {
                    void this.#downloadLayerImage(layerId);
                },
            },
            {
                label: `Send to ${otherSideLabel}`,
                action: () => {
                    void this.#sendLayerToOtherSide(layerId);
                },
            },
            {
                label: 'AI Background Removal',
                action: () => {
                    void RemoveLayerBackground(ctx, layerId);
                },
            },
            ...upscaleItem ? [upscaleItem] : [],
            {
                label: 'Center',
                action: () => {
                    CenterLayer(ctx, layerId);
                },
            },
            {
                label: 'Fit to Area',
                action: () => {
                    FitLayer(ctx, layerId);
                },
            },
            {
                label: 'Reset Transform',
                action: () => {
                    ResetLayer(ctx, layerId);
                },
            },
            {
                label: 'Flip Horizontal',
                action: () => {
                    FlipLayerHorizontal(ctx, layerId);
                },
            },
            {
                label: 'Flip Vertical',
                action: () => {
                    FlipLayerVertical(ctx, layerId);
                },
            },
            {
                label: 'Rotate Left',
                action: () => {
                    RotateLayer(ctx, layerId, -90);
                },
            },
            {
                label: 'Rotate Right',
                action: () => {
                    RotateLayer(ctx, layerId, 90);
                },
            },
        ];

        if (showMoveOptions) {
            menuItems.push(
                {
                    label: 'Move Forward',
                    action: () => {
                        this.#moveLayerForward(layerId);
                    },
                },
                {
                    label: 'Move Backward',
                    action: () => {
                        this.#moveLayerBackward(layerId);
                    },
                },
                {
                    label: 'Bring to Front',
                    action: () => {
                        this.#bringLayerToFront(layerId);
                    },
                },
                {
                    label: 'Bring to Back',
                    action: () => {
                        this.#bringLayerToBack(layerId);
                    },
                },
            );
        }

        menuItems.forEach(item => {
            const $item = document.createElement('button');
            $item.className = `layer-menu-item ${item.className || ''}`;
            $item.textContent = item.label;
            $item.addEventListener('click', () => {
                item.action();
                this.#closeMenu();
            });
            $menu.appendChild($item);
        });

        // Position menu at cursor (context menu) or below button (layer list)
        $menu.style.position = 'fixed';
        if (cursorPosition) {
            $menu.style.left = `${cursorPosition.x}px`;
            $menu.style.top = `${cursorPosition.y}px`;
        } else {
            const rect = buttonElement.getBoundingClientRect();
            $menu.style.top = `${rect.bottom + 5}px`;
            $menu.style.right = `${window.innerWidth - rect.right}px`;
        }

        document.body.appendChild($menu);

        // Clamp to viewport so the menu doesn't overflow off-screen
        const menuEdgePadding = 8;
        requestAnimationFrame(() => {
            const menuRect = $menu.getBoundingClientRect();

            if (menuRect.right > window.innerWidth) {
                $menu.style.left = `${window.innerWidth - menuRect.width - menuEdgePadding}px`;
                $menu.style.right = '';
            } else if (menuRect.left < menuEdgePadding) {
                $menu.style.left = `${menuEdgePadding}px`;
                $menu.style.right = '';
            }

            if (menuRect.bottom > window.innerHeight) {
                $menu.style.top = `${window.innerHeight - menuRect.height - menuEdgePadding}px`;
            } else if (menuRect.top < menuEdgePadding) {
                $menu.style.top = `${menuEdgePadding}px`;
            }
        });
        this.#activeMenu = $menu;

        setTimeout(() => {
            document.addEventListener('click', this.#handleOutsideClick);
        }, 0);
    }

    #handleOutsideClick = () => {
        this.#closeMenu();
    };

    #closeMenu() {
        if (this.#activeMenu) {
            this.#activeMenu.remove();
            this.#activeMenu = null;
            document.removeEventListener('click', this.#handleOutsideClick);
        }
    }

    // Point the upload map at the cutout so duplicate/send/save use it (stash original for revert).
    #moveLayerForward(layerId: string) {
        try {
            this.#currentEngine.moveLayerForward(layerId);
            this.#renderLayerList();
        } catch (error) {
            ShowError(error);
            throw error;
        }
    }

    #moveLayerBackward(layerId: string) {
        try {
            this.#currentEngine.moveLayerBackward(layerId);
            this.#renderLayerList();
        } catch (error) {
            ShowError(error);
            throw error;
        }
    }

    #bringLayerToFront(layerId: string) {
        try {
            this.#currentEngine.bringLayerToFront(layerId);
            this.#currentEngine.emit('significantChange', { layerId });
            this.#renderLayerList();
        } catch (error) {
            ShowError(error);
            throw error;
        }
    }

    #bringLayerToBack(layerId: string) {
        try {
            this.#currentEngine.sendToBackOfUserLayers(layerId);
            this.#currentEngine.emit('significantChange', { layerId });
            this.#renderLayerList();
        } catch (error) {
            ShowError(error);
            throw error;
        }
    }

    /** Notifies other selection owners (e.g. text drop) so selections stay exclusive */
    onLayerSelected?: (layerId: string) => void;

    /** Clear image selection from outside (e.g. when a text block gets selected) */
    deselectAll(): void {
        this.#deselectAllLayers(true);
        if (this.#selectedLayerId) {
            this.#selectedLayerId = null;
            this.#renderLayerList();
        }
    }

    getSelectedLayerId(): string | null {
        return this.#selectedLayerId;
    }

    /** The selected upload's Konva node (keyboard nudge etc.) */
    getSelectedNode(): Konva.Node | null {
        if (!this.#selectedLayerId) {
            return null;
        }
        const layer = this.#currentEngine.getLayer(this.#selectedLayerId);
        return layer instanceof ImageLayer ? layer.getImageNode() ?? null : null;
    }

    /** Delete with the undo toast (keyboard shortcuts, layer-list rows) */
    deleteLayerWithUndo(layerId: string): void {
        this.#deleteLayerWithUndo(layerId);
    }

    /** The visible side's engine (keyboard nudge reads it live) */
    currentEngine(): CustomizerEngine {
        return this.#currentEngine;
    }

    /** Whether an upload or AI process is running (page-level processing lock) */
    isProcessing(): boolean {
        return this.#isProcessing;
    }

    /** Wire up the text drop UI so its blocks appear as rows in the design layer lists */
    setTextDropUI(textDrop: TextDropUI): void {
        this.#textDrop = textDrop;
        document.addEventListener('coin-text-drop-changed', () => {
            this.#renderDesignTabLayerList();
        });
    }

    #selectLayer(layerId: string) {
        this.#deselectAllLayers();

        this.#selectedLayerId = layerId;
        this.onLayerSelected?.(layerId);

        // Attach HtmlTransformer to the image node for resize/rotate handles
        // Using HtmlTransformer instead of Konva.Transformer avoids clipping issues
        // when the image extends beyond the canvas bounds
        const layer = this.#currentEngine.getLayer(layerId);
        if (layer && 'getImageNode' in layer) {
            const imageLayer = layer as ImageLayer;
            imageLayer.select();
            const imageNode = imageLayer.getImageNode();
            if (imageNode) {
                this.#currentTransformer.attachTo(imageNode);
            }
        }

        this.#renderLayerList();
    }

    #deselectAllLayers(allEngines: boolean = false) {
        this.#closeMenu();

        // Deselect the currently selected layer (for wheel zoom state)
        if (this.#selectedLayerId) {
            const layer = this.#currentEngine.getLayer(this.#selectedLayerId);
            if (layer && 'deselect' in layer) {
                (layer as ImageLayer).deselect();
            }
        }

        // Detach HtmlTransformer to hide resize/rotate handles
        if (allEngines) {
            this.#frontTransformer.detach();
            this.#backTransformer.detach();
        } else {
            this.#currentTransformer.detach();
        }
    }

    #toggleLayerVisibility(layerId: string) {
        const layer = this.#currentEngine.getLayer(layerId);
        if (!layer) {
            return;
        }
        const isVisible = !layer.isVisible();
        layer.setVisible(isVisible);

        // Deselect if hiding the currently selected layer
        if (!isVisible && this.#selectedLayerId === layerId) {
            this.#deselectAllLayers();
        }

        this.#renderLayerList();
        this.#renderDesignTabLayerList();
        this.#currentEngine.emit('significantChange', { layerId });
    }

    #deleteLayer(layerId: string) {
        this.#deleteLayerWithUndo(layerId);
    }

    #nextCopyFilename(filename: string, side: 'front' | 'back'): string {
        const dot = filename.lastIndexOf('.');
        const base = dot >= 0 ? filename.slice(0, dot) : filename;
        const ext = dot >= 0 ? filename.slice(dot) : '';
        const rootMatch = /^(.+?)( \(copy(?: \d+)?\))$/.exec(base);
        const root = rootMatch ? rootMatch[1] : base;
        const used = new Set(
            Array.from(this.#uploadedImages.values()).
                filter(img => img.side === side).
                map(img => img.filename),
        );
        for (let i = 1; ; i++) {
            const suffix = i === 1 ? ' (copy)' : ` (copy ${i})`;
            const candidate = `${root}${suffix}${ext}`;
            if (!used.has(candidate)) {
                return candidate;
            }
        }
    }

    async #duplicateLayer(sourceLayerId: string): Promise<void> {
        try {
            const sourceInfo = this.#uploadedImages.get(sourceLayerId);
            if (!sourceInfo) {
                return;
            }

            const engine = sourceInfo.side === 'front' ? this.#frontEngine : this.#backEngine;
            const sourceLayer = engine.getLayer(sourceLayerId) as ImageLayer | undefined;
            if (!sourceLayer) {
                return;
            }

            const sourcePosition = sourceLayer.getPosition();
            const sourceScale = sourceLayer.getScale();
            const sourceRotation = sourceLayer.getRotation();
            const sourceUrl = sourceInfo.permanentUrl ?? sourceInfo.url;

            const newLayerID = `img-${sourceInfo.side}-${UUID()}`;
            const newLayer = new ImageLayer(newLayerID, {
                userEditable: true,
                resizable: true,
                zIndex: 7,
            });

            await newLayer.setImage(sourceUrl);

            const zone = engine.getImageZone('coin-center');
            if (zone?.maskUrl) {
                await newLayer.setMask(zone.maskUrl, zone.maskPosition, {
                    width: zone.bounds.width,
                    height: zone.bounds.height,
                });
            }

            const duplicateInfo: uploadedImage = {
                layerID: newLayerID,
                uploadID: sourceInfo.uploadID,
                side: sourceInfo.side,
                url: sourceUrl,
                previewUrl: sourceInfo.previewUrl,
                permanentUrl: sourceInfo.permanentUrl,
                filename: this.#nextCopyFilename(sourceInfo.filename, sourceInfo.side),
                claim: sourceInfo.claim,
            };
            this.#uploadedImages.set(newLayerID, duplicateInfo);

            await engine.addLayer(newLayerID, newLayer);
            engine.bringLayerToFront(newLayerID);

            // Offset the duplicate so it's visibly separate from the source.
            const DUPLICATE_OFFSET_PX = 100;
            newLayer.setPosition(sourcePosition.x + DUPLICATE_OFFSET_PX, sourcePosition.y + DUPLICATE_OFFSET_PX);
            newLayer.setScale(sourceScale.x, sourceScale.y);
            if (sourceRotation !== 0) {
                newLayer.setRotation(sourceRotation);
            }
            engine.batchDraw();

            this.#setupLayerClickListener(newLayer, newLayerID);

            if (this.#currentSide === sourceInfo.side) {
                this.#selectLayer(newLayerID);
            } else {
                this.#renderLayerList();
            }

            toast.Success('Layer duplicated');

            const capturedDuplicate = { ...duplicateInfo };
            const capturedMaskUrl = zone?.maskUrl;
            const capturedMaskPosition = zone?.maskPosition;
            engine.addAction({
                type: 'DUPLICATE_IMAGE',
                layerId: newLayerID,
                description: `Duplicate image ${sourceInfo.filename}`,
                undo: () => {
                    this.#performLayerDeletion(newLayerID, sourceInfo.side);
                },
                redo: () => {
                    void this.#restoreDeletedLayer(
                        engine,
                        capturedDuplicate,
                        sourceUrl,
                        { x: sourcePosition.x + DUPLICATE_OFFSET_PX, y: sourcePosition.y + DUPLICATE_OFFSET_PX },
                        sourceScale,
                        sourceRotation,
                        capturedMaskUrl,
                        capturedMaskPosition,
                    );
                },
            });
        } catch (error) {
            toast.Error('Failed to duplicate image');
            console.error('[ImageUploadUI] Duplicate error:', error);
        }
    }

    async #downloadLayerImage(layerId: string): Promise<void> {
        const info = this.#uploadedImages.get(layerId);
        if (!info) {
            toast.Error('Image not found');
            return;
        }

        // Permanent (CDN) URL outlives the blob URL, so prefer it when present.
        const url = info.permanentUrl ?? info.url;
        const filename = info.filename || 'coin-image';

        try {
            await DownloadURL(filename, { url });
        } catch (error) {
            // A blocked CORS fetch can't produce a blob; fall back to letting the
            // browser navigate to the file with the download hint set.
            const $link = document.createElement('a');
            $link.href = url;
            $link.download = filename;
            $link.target = '_blank';
            $link.rel = 'noopener';
            document.body.appendChild($link);
            $link.click();
            $link.remove();
        }
    }

    #deleteLayerWithUndo(layerId: string) {
        // Keyboard delete bypasses the pointer lock — a layer mutation
        // mid-process would strand the result on a deleted layer
        if (this.#isProcessing) {
            return;
        }
        try {
            const uploadInfo = this.#uploadedImages.get(layerId);
            if (!uploadInfo) {
                return;
            }

            // Capture the engine for this layer's side (not #currentEngine, since the user
            // may switch sides before undoing)
            const engine = uploadInfo.side === 'front' ? this.#frontEngine : this.#backEngine;
            const transformer = uploadInfo.side === 'front' ? this.#frontTransformer : this.#backTransformer;
            const layer = engine.getLayer(layerId) as ImageLayer | undefined;

            if (!layer) {
                return;
            }

            // Capture state before deletion for undo
            const capturedPosition = layer.getPosition();
            const capturedScale = layer.getScale();
            const capturedRotation = layer.getRotation();
            const capturedUrl = uploadInfo.permanentUrl ?? uploadInfo.url;
            const capturedUploadInfo = { ...uploadInfo };
            const zone = engine.getImageZone('coin-center');
            const capturedMaskUrl = zone?.maskUrl;
            const capturedMaskPosition = zone?.maskPosition;

            // Perform deletion
            if (this.#selectedLayerId === layerId) {
                layer.deselect();
                transformer.detach();
                this.#selectedLayerId = null;
            }

            engine.removeLayer(layerId);

            // Revoke blob URL to prevent memory leak (only if we have a permanent URL to fall back on)
            if (uploadInfo.url.startsWith('blob:') && uploadInfo.permanentUrl) {
                URL.revokeObjectURL(uploadInfo.url);
            }

            this.#uploadedImages.delete(layerId);

            this.#renderLayerList();

            toast.Undo('Image removed', () => {
                engine.undoLocal();
            });

            // Register undo/redo action on the layer's engine
            engine.addAction({
                type: 'DELETE_IMAGE',
                layerId,
                description: `Delete image ${capturedUploadInfo.filename}`,
                undo: () => {
                    void this.#restoreDeletedLayer(
                        engine,
                        capturedUploadInfo,
                        capturedUrl,
                        capturedPosition,
                        capturedScale,
                        capturedRotation,
                        capturedMaskUrl,
                        capturedMaskPosition,
                    );
                },
                redo: () => {
                    this.#performLayerDeletion(layerId, capturedUploadInfo.side);
                },
            });
        } catch (error) {
            toast.Error('Failed to remove image');
            console.error('[ImageUploadUI] Delete error:', error);
        }
    }

    async #restoreDeletedLayer(
        engine: CustomizerEngine,
        capturedUploadInfo: uploadedImage,
        capturedUrl: string,
        capturedPosition: { x: number; y: number },
        capturedScale: { x: number; y: number },
        capturedRotation: number,
        capturedMaskUrl: string | undefined,
        capturedMaskPosition: { x: number; y: number } | undefined,
    ): Promise<void> {
        try {
            const imageLayer = new ImageLayer(capturedUploadInfo.layerID, {
                userEditable: true,
                resizable: true,
                zIndex: 7,
            });

            await imageLayer.setImage(capturedUrl);

            if (capturedMaskUrl) {
                await imageLayer.setMask(capturedMaskUrl, capturedMaskPosition);
            }

            // Re-add upload tracking BEFORE addLayer (which triggers autosave)
            const restoredInfo: uploadedImage = {
                ...capturedUploadInfo,
                url: capturedUrl,
            };
            this.#uploadedImages.set(capturedUploadInfo.layerID, restoredInfo);

            await engine.addLayer(capturedUploadInfo.layerID, imageLayer);

            // Apply saved transforms AFTER addLayer (since attachTo resets position)
            imageLayer.setPosition(capturedPosition.x, capturedPosition.y);
            imageLayer.setScale(capturedScale.x, capturedScale.y);
            if (capturedRotation !== 0) {
                imageLayer.setRotation(capturedRotation);
            }

            engine.batchDraw();

            this.#setupLayerClickListener(imageLayer, capturedUploadInfo.layerID);

            if (this.#currentSide === capturedUploadInfo.side) {
                this.#selectLayer(capturedUploadInfo.layerID);
            } else {
                this.#selectedLayerId = capturedUploadInfo.layerID;
                this.#renderLayerList();
            }
        } catch (error) {
            toast.Error('Failed to undo image removal');
            console.error('[ImageUploadUI] Failed to restore deleted layer:', error);
        }
    }

    #performLayerDeletion(layerId: string, side: 'front' | 'back') {
        const engine = side === 'front' ? this.#frontEngine : this.#backEngine;
        const transformer = side === 'front' ? this.#frontTransformer : this.#backTransformer;
        const uploadInfo = this.#uploadedImages.get(layerId);

        if (this.#selectedLayerId === layerId) {
            const layer = engine.getLayer(layerId) as ImageLayer | undefined;
            if (layer) {
                layer.deselect();
            }
            transformer.detach();
            this.#selectedLayerId = null;
        }

        engine.removeLayer(layerId);

        // Revoke blob URL to prevent memory leak (only if we have a permanent URL to fall back on)
        if (uploadInfo?.url.startsWith('blob:') && uploadInfo.permanentUrl) {
            URL.revokeObjectURL(uploadInfo.url);
        }

        this.#uploadedImages.delete(layerId);

        this.#renderLayerList();
    }

    async #sendLayerToOtherSide(layerId: string): Promise<void> {
        const uploadInfo = this.#uploadedImages.get(layerId);
        if (!uploadInfo) {
            return;
        }

        const sourceSide = uploadInfo.side;
        const targetSide: 'front' | 'back' = sourceSide === 'front' ? 'back' : 'front';
        const sourceEngine = sourceSide === 'front' ? this.#frontEngine : this.#backEngine;
        const targetEngine = targetSide === 'front' ? this.#frontEngine : this.#backEngine;
        const sourceTransformer = sourceSide === 'front' ? this.#frontTransformer : this.#backTransformer;
        const layer = sourceEngine.getLayer(layerId) as ImageLayer | undefined;

        if (!layer) {
            return;
        }

        // Capture state before removal
        const position = layer.getPosition();
        const scale = layer.getScale();
        const rotation = layer.getRotation();
        const url = uploadInfo.permanentUrl ?? uploadInfo.url;
        const capturedUploadInfo = { ...uploadInfo };

        this.#processingCount++;
        this.#setInteractionEnabled(false);
        let lockReleased = false;

        try {
            // Prepare the new layer on the target side BEFORE removing from source,
            // so if loading fails the image isn't lost from both sides
            const newLayer = new ImageLayer(layerId, {
                userEditable: true,
                resizable: true,
                zIndex: 7,
            });

            await newLayer.setImage(url);

            // Apply mask from the target engine (each side may have different mask config)
            const targetZone = targetEngine.getImageZone('coin-center');
            if (targetZone?.maskUrl) {
                await newLayer.setMask(targetZone.maskUrl, targetZone.maskPosition);
            }

            // Target layer is ready — now safe to remove from source
            if (this.#selectedLayerId === layerId) {
                layer.deselect();
                sourceTransformer.detach();
                this.#selectedLayerId = null;
            }

            sourceEngine.removeLayer(layerId);
            this.#uploadedImages.delete(layerId);
            this.#renderLayerList();

            // Add to target side
            const restoredInfo: uploadedImage = {
                ...capturedUploadInfo,
                side: targetSide,
                url,
            };
            this.#uploadedImages.set(layerId, restoredInfo);

            await targetEngine.addLayer(layerId, newLayer);

            newLayer.setPosition(position.x, position.y);
            newLayer.setScale(scale.x, scale.y);
            if (rotation !== 0) {
                newLayer.setRotation(rotation);
            }

            targetEngine.batchDraw();
            this.#setupLayerClickListener(newLayer, layerId);

            // Release the processing lock before switching sides — setSide() bails
            // early if #isProcessing is true, and we need it to actually switch now.
            this.#processingCount--;
            lockReleased = true;
            if (!this.#isProcessing) {
                this.#setInteractionEnabled(true);
            }

            // Switch to the target side so the user sees the moved image
            const $radio = document.querySelector<HTMLInputElement>(
                `input[name="front-back-radio"][value="${targetSide}"]`,
            );
            if ($radio) {
                $radio.checked = true;
                $radio.dispatchEvent(new Event('change'));
            }

            // Select the layer after the side switch settles
            requestAnimationFrame(() => {
                this.#selectLayer(layerId);
            });

            // Register undo/redo on the target engine (user is now viewing that side)
            targetEngine.addAction({
                type: 'SEND_TO_OTHER_SIDE',
                layerId,
                description: `Send image "${capturedUploadInfo.filename}" to ${targetSide} side`,
                undo: async () => {
                    try {
                        await this.#transferLayer(layerId, targetSide, sourceSide, url, position, scale, rotation, capturedUploadInfo);
                    } catch (err) {
                        ShowError(err);
                    }
                },
                redo: async () => {
                    try {
                        await this.#transferLayer(layerId, sourceSide, targetSide, url, position, scale, rotation, capturedUploadInfo);
                    } catch (err) {
                        ShowError(err);
                    }
                },
            });

            toast.Undo(`Image sent to ${targetSide} side`, () => {
                targetEngine.undoLocal();
            });
        } catch (error) {
            ShowError(error);
        } finally {
            // Only decrement if we didn't already release the lock mid-operation
            if (!lockReleased) {
                this.#processingCount--;
                if (!this.#isProcessing) {
                    this.#setInteractionEnabled(true);
                }
            }
        }
    }

    /**
     * Transfer a layer between sides. Used by undo/redo for send-to-other-side.
     */
    async #transferLayer(
        layerId: string,
        fromSide: 'front' | 'back',
        toSide: 'front' | 'back',
        url: string,
        position: { x: number; y: number },
        scale: { x: number; y: number },
        rotation: number,
        baseUploadInfo: uploadedImage,
    ): Promise<void> {
        // Undo/redo of a send while another process runs would race it — drop the request
        if (this.#isProcessing) {
            return;
        }

        const fromEngine = fromSide === 'front' ? this.#frontEngine : this.#backEngine;
        const toEngine = toSide === 'front' ? this.#frontEngine : this.#backEngine;
        const fromTransformer = fromSide === 'front' ? this.#frontTransformer : this.#backTransformer;

        this.#processingCount++;
        this.#setInteractionEnabled(false);
        let lockReleased = false;

        try {
            const existingLayer = fromEngine.getLayer(layerId) as ImageLayer | undefined;
            if (existingLayer) {
                if (this.#selectedLayerId === layerId) {
                    existingLayer.deselect();
                    fromTransformer.detach();
                    this.#selectedLayerId = null;
                }
                fromEngine.removeLayer(layerId);
            }
            this.#uploadedImages.delete(layerId);

            const newLayer = new ImageLayer(layerId, {
                userEditable: true,
                resizable: true,
                zIndex: 7,
            });

            await newLayer.setImage(url);

            // Apply mask from the destination engine (each side may have different mask config)
            const destZone = toEngine.getImageZone('coin-center');
            if (destZone?.maskUrl) {
                await newLayer.setMask(destZone.maskUrl, destZone.maskPosition);
            }

            this.#uploadedImages.set(layerId, { ...baseUploadInfo, side: toSide, url });

            await toEngine.addLayer(layerId, newLayer);

            newLayer.setPosition(position.x, position.y);
            newLayer.setScale(scale.x, scale.y);
            if (rotation !== 0) {
                newLayer.setRotation(rotation);
            }

            toEngine.batchDraw();
            this.#setupLayerClickListener(newLayer, layerId);

            // Release the processing lock before switching sides — setSide()
            // bails early if #isProcessing is true, and we need it to switch now
            this.#processingCount--;
            lockReleased = true;
            if (!this.#isProcessing) {
                this.#setInteractionEnabled(true);
            }

            const $radio = document.querySelector<HTMLInputElement>(
                `input[name="front-back-radio"][value="${toSide}"]`,
            );
            if ($radio) {
                $radio.checked = true;
                $radio.dispatchEvent(new Event('change'));
            }

            requestAnimationFrame(() => {
                this.#selectLayer(layerId);
            });

            this.#renderLayerList();
        } finally {
            // Only decrement if we didn't already release the lock mid-operation
            if (!lockReleased) {
                this.#processingCount--;
                if (!this.#isProcessing) {
                    this.#setInteractionEnabled(true);
                }
            }
        }
    }

    // ============ Processing Lock ============
    /**
     * Lock/unlock the whole editing surface around an async process (upload,
     * AI upscale/background removal, send-to-other-side). #currentSide is
     * captured when a process starts and setSide() bails while processing, so
     * any edit that slips through mid-process lands on the frozen side —
     * disabling only the side radios wasn't enough (#6553).
     */
    #setInteractionEnabled(enabled: boolean) {
        // Blanket lock: inert blocks clicks, focus, and keyboard for the whole
        // builder subtree, catching every control the targeted disables below
        // would have to enumerate ('add text', 'copy design', quantity, ...).
        // The targeted disables stay for visual affordance and as a fallback.
        document.getElementById('main')?.toggleAttribute('inert', !enabled);

        // The toggle switch and segmented buttons are separate controls that
        // proxy to the radios — disabling only the radios leaves them live
        const $sideControls = document.querySelectorAll<HTMLInputElement | HTMLButtonElement>(
            'input[name="front-back-radio"], #coin-side-toggle-switch, .segmented-btn',
        );
        $sideControls.forEach($el => {
            $el.disabled = !enabled;
        });

        if (this.#fileInput) {
            this.#fileInput.disabled = !enabled;
        }

        for (const $el of [
            this.#uploadInputContainer,
            this.#layerListContainer,
            this.#mobileUploadZone,
            this.#lgUploadZone,
            this.#mobileLayersPanel,
            this.#designTabUploads,
            this.#lgDesignUploads,
            this.#designTabLayerList,
            this.#lgDesignLayerList,
        ]) {
            $el?.classList.toggle('processing-locked', !enabled);
        }

        this.#frontEngine.getStage().listening(enabled);
        this.#backEngine.getStage().listening(enabled);
        this.#frontTransformer.setEnabled(enabled);
        this.#backTransformer.setEnabled(enabled);

        this.#sortable?.option('disabled', !enabled);

        if (!enabled) {
            this.#closeMenu();
            this.#designList?.closeActionBar();
        }
    }

    /**
     * Push a programmatically generated image (e.g. a QR code PNG) through the
     * standard upload pipeline. The blob lands in the uploads table just like a
     * file picked from disk, and the existing onStart/onComplete hooks add it
     * as a layer on the current side.
     */
    public async addBlob(blob: Blob, filename: string): Promise<void> {
        if (!this.#uploader) {
            throw new Error('uploader not initialized');
        }
        await this.#uploader.newUploadsFromBlobs([{
            name: filename,
            mime: blob.type || 'application/octet-stream',
            data: blob,
        }]);
    }

    public setSide(side: 'front' | 'back') {
        // Prevent side switching while upload or processing (e.g., background removal) is in progress
        if (this.#isProcessing) {
            return;
        }

        // Deselect all layers on both sides before switching
        this.#deselectAllLayers(true);

        this.#currentSide = side;
        this.#selectedLayerId = null; // Clear selection when switching sides
        this.#printWarning.refresh(); // The warnings describe the side on screen
        this.#renderLayerList(); // Re-render to show only current side's layers

        // Force a redraw of the current engine's stage to ensure images render
        // This is especially important for the back canvas which starts hidden
        this.#currentEngine.batchDraw();
    }

    // ============ Utilities ============
    #setupLayerClickListener(imageLayer: ImageLayer, layerId: string) {
        imageLayer.on('click tap dragstart', () => {
            this.#selectLayer(layerId);
        });
        // Track when Konva drag ends to prevent deselection from document click handler
        imageLayer.on('dragend', () => {
            this.#lastKonvaDragEndTime = Date.now();
        });
    }

    #setupStageClickListeners() {
        const frontStage = this.#frontEngine.getStage();
        const backStage = this.#backEngine.getStage();

        const handleStageClick = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
            // A click that Konva targeted at a text node belongs to TextDropUI — the text
            // is topmost at that point, so don't steal the selection for an image below it
            if (e.target instanceof Konva.Text || e.target instanceof Konva.TextPath) {
                return;
            }

            // Use engine's z-order-aware hit testing instead of Konva's bounding-box detection
            const stage = e.currentTarget.getStage();
            const pointer = stage?.getPointerPosition();
            if (pointer) {
                const topLayer = this.#currentEngine.getTopmostImageLayerAt(pointer.x, pointer.y);
                if (topLayer) {
                    this.#selectLayer(topLayer);
                    return;
                }
            }

            this.#deselectAllLayers();
            this.#selectedLayerId = null;
            this.#renderLayerList();
        };

        frontStage.on('click tap', handleStageClick);
        backStage.on('click tap', handleStageClick);

        // Layer-item selectors are matched directly because renderLayerList() may have
        // removed the clicked element from the DOM before this handler runs
        SetupClickAwayDeselect({
            protectedSelectors:
                '#front-canvas, #back-canvas, #coin-preview-container, ' +
                '#uploaded-images-list, #mobile-image-layers, #design-uploads, #lg-design-uploads, ' +
                '.upload-layer-item, .add-image-btn, .mobile-layer-thumb-wrapper, .design-layer-row, .design-layer-menu-btn, .layer-action-bar, ' +
                '.layer-menu-dropdown, .html-transformer-overlay',
            hasSelection: () => this.#selectedLayerId !== null,
            // 100ms matches DRAG_END_DEBOUNCE_MS in HtmlTransformer
            justFinishedDragging: () => this.#frontTransformer.justFinishedDragging() ||
                this.#backTransformer.justFinishedDragging() ||
                Date.now() - this.#lastKonvaDragEndTime < 100,
            deselect: () => {
                this.#deselectAllLayers();
                this.#selectedLayerId = null;
                this.#designList?.closeActionBar();
                this.#renderLayerList();
            },
        });
    }

    #setupContextMenu() {
        const showMenuAt = (layerId: string, e: MouseEvent) => {
            // contextmenu is a DOM event on the stage container, so it fires
            // even with Konva listening() off — honor the processing lock
            if (this.#isProcessing) {
                return;
            }
            this.#selectLayer(layerId);
            this.#showLayerMenu(layerId, document.body as HTMLElement, {
                x: e.clientX,
                y: e.clientY,
            });
        };

        // Listen on canvas containers for right-clicks that reach the Konva stage directly
        const setupForStage = (stage: Konva.Stage) => {
            const container = stage.container();
            container.addEventListener('contextmenu', (e: MouseEvent) => {
                e.preventDefault();

                // Convert client coordinates to stage coordinates (accounts for CSS scaling)
                const containerRect = container.getBoundingClientRect();
                const scaleX = stage.width() / containerRect.width;
                const scaleY = stage.height() / containerRect.height;
                const pos = {
                    x: (e.clientX - containerRect.left) * scaleX,
                    y: (e.clientY - containerRect.top) * scaleY,
                };

                const node = stage.getIntersection(pos);
                if (!node) {
                    return;
                }

                const layerId = this.#findLayerIdFromKonvaNode(node);
                if (!layerId) {
                    return;
                }

                showMenuAt(layerId, e);
            });
        };

        setupForStage(this.#frontEngine.getStage());
        setupForStage(this.#backEngine.getStage());

        // The HtmlTransformer overlay (z-index 1000, pointer-events: auto on the drag area)
        // intercepts right-clicks when a layer is selected. Listen on document and check if
        // the click target is inside a transformer overlay.
        document.addEventListener('contextmenu', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.html-transformer-overlay')) {
                return;
            }

            e.preventDefault();

            if (this.#selectedLayerId) {
                showMenuAt(this.#selectedLayerId, e);
            }
        });
    }

    /** Walk up the Konva parent chain to find which uploaded image layer a node belongs to */
    #findLayerIdFromKonvaNode(target: Konva.Node): string | null {
        let node: Konva.Node | null = target;
        while (node) {
            const nodeName = node.name();
            if (nodeName && this.#uploadedImages.has(nodeName)) {
                return nodeName;
            }
            node = node.parent;
        }
        return null;
    }

    /** Called by HtmlTransformer when it re-targets to a different image layer.
     *  The transformer already re-attached itself — just update selection bookkeeping. */
    #onTransformerTargetChange(layerId: string): void {
        // Deselect the old layer's state (wheel zoom flag, etc.)
        if (this.#selectedLayerId) {
            const oldLayer = this.#currentEngine.getLayer(this.#selectedLayerId);
            if (oldLayer && 'deselect' in oldLayer) {
                (oldLayer as ImageLayer).deselect();
            }
        }

        this.#selectedLayerId = layerId;
        const newLayer = this.#currentEngine.getLayer(layerId);
        if (newLayer && 'select' in newLayer) {
            (newLayer as ImageLayer).select();
        }
        this.#renderLayerList();
    }

    // ============ Cleanup ============
    public destroy() {
        // Revoke all blob URLs to prevent memory leaks
        for (const uploadInfo of this.#uploadedImages.values()) {
            if (uploadInfo.url.startsWith('blob:')) {
                URL.revokeObjectURL(uploadInfo.url);
            }
        }

        this.#sortable?.destroy();
        this.#sortable = null;

        this.#frontTransformer.destroy();
        this.#backTransformer.destroy();

        this.#uploadedImages.clear();
        this.#selectedLayerId = null;
        this.#closeMenu();
        if (this.#layerListContainer) {
            this.#layerListContainer.innerHTML = '';
            this.#layerListContainer.classList.add('hidden');
        }
        if (this.#uploadInputContainer) {
            this.#uploadInputContainer.classList.remove('d-none');
        }
        if (this.#uploadHeading) {
            this.#uploadHeading.classList.remove('d-none');
        }

        if (this.#mobileLayersScroll) {
            this.#mobileLayersScroll.innerHTML = '';
        }
        if (this.#mobileLayersPanel) {
            this.#mobileLayersPanel.classList.add('d-none');
        }
        if (this.#mobileAddImagesBtn) {
            this.#mobileAddImagesBtn.classList.remove('d-none');
        }

        document.dispatchEvent(new CustomEvent('coin-uploads-changed'));
    }

    /**
     * Get uploadIDs that don't have permanent URLs yet.
     * Only these need to be sent to the keep endpoint.
     */
    getUploadIDs(): number[] {
        const ids: number[] = [];
        for (const img of this.#uploadedImages.values()) {
            // Only return IDs that don't have a permanent URL cached
            if (img.uploadID > 0 && !img.permanentUrl) {
                ids.push(img.uploadID);
            }
        }
        return ids;
    }

    /**
     * Cache permanent URLs after keep endpoint succeeds.
     * This prevents needing to call keep again on subsequent saves.
     */
    setPermanentUrls(urls: Map<number, string>): void {
        for (const img of this.#uploadedImages.values()) {
            const permanentUrl = urls.get(img.uploadID);
            if (permanentUrl) {
                img.permanentUrl = permanentUrl;
                img.claim = UploadClaimFromURL(permanentUrl);
                uploadClaims.Register([{ uploadID: img.uploadID, claim: img.claim }]);
            }
        }
    }

    /**
     * Check if there are uploaded images for a given side.
     */
    hasUploads(side: 'front' | 'back'): boolean {
        for (const img of this.#uploadedImages.values()) {
            if (img.side === side) {
                return true;
            }
        }
        return false;
    }

    /** Check if there are visible (non-hidden) uploads for a given side. */
    hasVisibleUploads(side: 'front' | 'back'): boolean {
        const engine = side === 'front' ? this.#frontEngine : this.#backEngine;
        for (const img of this.#uploadedImages.values()) {
            if (img.side !== side) {
                continue;
            }
            if (engine.getLayer(img.layerID)?.isVisible()) {
                return true;
            }
        }
        return false;
    }

    /**
     * Get all upload IDs for a given side.
     */
    getUploadIDsForSide(side: 'front' | 'back'): number[] {
        const ids: number[] = [];
        for (const img of this.#uploadedImages.values()) {
            if (img.side !== side || img.uploadID <= 0) {
                continue;
            }
            // Exclude hidden layers from cart uploads
            const engine = side === 'front' ? this.#frontEngine : this.#backEngine;
            const layer = engine.getLayer(img.layerID);
            if (layer && !layer.isVisible()) {
                continue;
            }
            ids.push(img.uploadID);
        }
        return ids;
    }

    /**
     * Get all upload IDs (both sides).
     */
    getAllUploadIDs(): number[] {
        const ids: number[] = [];
        for (const img of this.#uploadedImages.values()) {
            if (img.uploadID > 0) {
                ids.push(img.uploadID);
            }
        }
        return ids;
    }

    /** Remove all uploaded images from both engines.
     * @param revokeUrls — revoke blob URLs (skip when images will be immediately restored) */
    clearAll(revokeUrls = true): void {
        for (const [layerID, info] of this.#uploadedImages) {
            const engine = info.side === 'front' ? this.#frontEngine : this.#backEngine;
            engine.removeLayer(layerID);
            if (revokeUrls && info.url.startsWith('blob:')) {
                URL.revokeObjectURL(info.url);
            }
        }
        this.#uploadedImages.clear();
        this.#selectedLayerId = null;
        this.#designList?.closeActionBar();
        this.#renderLayerList();
        this.#renderDesignTabLayerList();
    }

    /**
     * Get current image state for persistence.
     * Uses cached permanent URLs from setPermanentUrls().
     */
    getState(): ImageState[] {
        const states: ImageState[] = [];

        for (const img of this.#uploadedImages.values()) {
            const engine = img.side === 'front' ? this.#frontEngine : this.#backEngine;
            const layer = engine.getLayer(img.layerID) as ImageLayer | undefined;

            if (!layer) {
                continue;
            }

            const position = layer.getPosition();
            const scale = layer.getScale();
            const rotation = layer.getRotation();
            const naturalSize = layer.getNaturalSize();
            const actualZIndex = layer.getActualZIndex();

            states.push({
                uploadID: img.uploadID,
                side: img.side,
                position,
                scale,
                rotation,
                naturalSize,
                actualZIndex,
                url: img.permanentUrl || img.url,
                filename: img.filename,
                visible: layer.isVisible(),
                isUpscaled: layer.hasUpscaled(),
                upscaledUploadID: layer.getUpscaledUploadID(),
            });
        }

        return states;
    }

    /**
     * Restore images from saved state.
     * @param states Array of saved image states with permanent URLs
     */
    async restoreState(states: ImageState[]): Promise<void> {
        // Sort by actualZIndex (lower first) so images are added in the correct stacking order
        // This preserves the front/back ordering the user set
        const sortedStates = [...states].sort((a, b) => {
            const aIndex = a.actualZIndex ?? 0;
            const bIndex = b.actualZIndex ?? 0;
            return aIndex - bIndex;
        });

        for (const state of sortedStates) {
            if (!state.url) {
                console.warn(`[ImageUploadUI] No URL for uploadID ${state.uploadID}, skipping`);
                continue;
            }

            const targetEngine = state.side === 'front' ? this.#frontEngine : this.#backEngine;
            const layerID = `img-${state.side}-${UUID()}`;

            const imageLayer = new ImageLayer(layerID, {
                userEditable: true,
                resizable: true,
                zIndex: 7,
            });

            try {
                await imageLayer.setImage(state.url);

                // url is already the upscaled result; mark it so it isn't re-upscaled.
                if (state.isUpscaled) {
                    imageLayer.restoreUpscaledState(state.upscaledUploadID);
                }

                // Apply mask (without coverSize since we'll set saved scale)
                const zone = targetEngine.getImageZone('coin-center');
                if (zone?.maskUrl) {
                    await imageLayer.setMask(zone.maskUrl, zone.maskPosition);
                }

                // Track the restored image BEFORE addLayer (which triggers autosave)
                const uploadInfo: uploadedImage = {
                    layerID,
                    uploadID: state.uploadID,
                    side: state.side,
                    url: state.url,
                    filename: state.filename || `img${state.uploadID}.jpg`,
                    permanentUrl: state.url, // Already have permanent URL, cache it
                    claim: UploadClaimFromURL(state.url),
                };
                this.#uploadedImages.set(layerID, uploadInfo);
                uploadClaims.Register([{ uploadID: uploadInfo.uploadID, claim: uploadInfo.claim }]);

                // addLayer calls attachTo which re-calls setImage, resetting position
                await targetEngine.addLayer(layerID, imageLayer);

                // Apply saved transforms AFTER addLayer (since attachTo resets position)
                // Validate saved values before applying to catch any serialization issues
                if (Number.isFinite(state.position.x) && Number.isFinite(state.position.y)) {
                    imageLayer.setPosition(state.position.x, state.position.y);
                }

                // Calculate adjusted scale if the permanent URL image has different dimensions
                // This preserves the visual size even if the server processed the image
                let adjustedScaleX = state.scale.x;
                let adjustedScaleY = state.scale.y;

                if (state.naturalSize && state.naturalSize.width > 0 && state.naturalSize.height > 0) {
                    const currentNaturalSize = imageLayer.getNaturalSize();
                    if (currentNaturalSize.width > 0 && currentNaturalSize.height > 0) {
                        // Adjust scale to preserve visual size if dimensions differ
                        const widthRatio = state.naturalSize.width / currentNaturalSize.width;
                        const heightRatio = state.naturalSize.height / currentNaturalSize.height;
                        adjustedScaleX = state.scale.x * widthRatio;
                        adjustedScaleY = state.scale.y * heightRatio;
                    }
                }

                if (Number.isFinite(adjustedScaleX) && Number.isFinite(adjustedScaleY) &&
                    adjustedScaleX !== 0 && adjustedScaleY !== 0) {
                    imageLayer.setScale(adjustedScaleX, adjustedScaleY);
                }

                if (state.rotation !== 0 && Number.isFinite(state.rotation)) {
                    imageLayer.setRotation(state.rotation);
                }

                if (state.visible === false) {
                    imageLayer.setVisible(false);
                }

                targetEngine.batchDraw();
                this.#setupLayerClickListener(imageLayer, layerID);
            } catch (error) {
                console.error(`[ImageUploadUI] Failed to restore image ${state.uploadID}:`, error);
            }
        }

        this.#renderLayerList();
    }
}
